import {
  CopyOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  Button,
  Form,
  Input,
  Popconfirm,
  Spin,
  Table,
  Upload,
  message,
  Modal,
} from "antd";

import axios from "axios";
import { useEffect, useState } from "react";
import TwoFactAuth from "../../TwoFactAuth/TwoFactAuth";
import { useSelector } from "react-redux";

const UploadDataBPGS = () => {
  const [ApiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const { email } = useSelector((state) => state.persistedReducer.user);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [updateFile, setUpdateFile] = useState(null);
  const [ext, setExt] = useState(null);
  const [twoFAAction, setTwoFAAction] = useState(null);

  // States for 2FA on delete only
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);

  useEffect(() => {
    getData();
  }, []);

  const getData = () => {
    setLoading(true);
    const environmentType = "/getUploadBPGSFiles";
    axios
      .get(process.env.REACT_APP_API_URL + environmentType)
      .then((result) => {
        let data = result.data;
        let newArr = [];
        data.map((x, i) => {
          newArr.push({
            no: i + 1,
            file: x.name,
            date_time: new Date(x.createdAt).toLocaleString(),
            action: x,
            id: x._id,
            fileLink: x.link,
          });
        });
        setApiData(newArr);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  useEffect(() => {
    if (!selectedFile) {
      return;
    }
    const parts = selectedFile.link?.split(".");

    setExt(parts.length > 1 ? parts.pop().toLowerCase() : null);
  }, [selectedFile]);

  const downloadFile = (id) => {
    const environmentType = "downloadBPGSData";
    ApiData.map((x) => {
      if (id === x.id) {
        window.location.href = `${process.env.REACT_APP_API_URL}/${environmentType}/${x.fileLink}?token=WgMLYoPjS56HqtD`;
      }
    });
  };

  const copyDownloadFileLink = (id) => {
    const environmentType = "downloadBPGSData";
    ApiData.map((x) => {
      if (id === x.id) {
        let copyText = `${process.env.REACT_APP_API_URL}/${environmentType}/${x.fileLink}?token=WgMLYoPjS56HqtD`;
        navigator.clipboard.writeText(copyText);
        messageApi.open({
          type: "success",
          content: "link has been copied to the clipboard",
        });
      }
    });
  };

  // Called AFTER 2FA is successfully verified for delete
  const confirmedDelete = (data) => {
    let data2 = {
      id: data._id,
      name: data.link,
    };
    setLoading(true);
    const environmentType = "/deleteUploadBPGSFiles";

    axios
      .post(process.env.REACT_APP_API_URL + environmentType, data2)
      .then(() => {
        messageApi.open({
          type: "success",
          content: "File has been deleted",
        });
        getData();
        setLoading(false);
        setIs2FAModalOpen(false);
      })
      .catch((err) => {
        setLoading(false);
        setIs2FAModalOpen(false);
        messageApi.open({
          type: "error",
          content: err?.response?.data?.message || "Failed to delete",
        });
        console.log(err);
      });
  };

  const handleUpdate = async () => {
    try {
      if (!updateFile) {
        messageApi.warning("Please select a file");
        return;
      }
      setLoading(true);
      const formData = new FormData();

      formData.append("id", selectedFile._id);
      formData.append("myfile", updateFile);

      await axios.post(
        `${process.env.REACT_APP_API_URL}/updateUploadBPGSFiles`,
        formData,
      );

      messageApi.success("File updated successfully");

      setIsOpen(false);
      setSelectedFile(null);
      setUpdateFile(null);

      getData();
      setLoading(false);
      setIs2FAModalOpen(false);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || "Failed to update file");
    } finally {
      setLoading(false);
    }
  };

  // When user clicks delete icon, open 2FA modal
  const handleDeleteClick = (data) => {
    confirmedDelete(data);
  };

  const handleUpdateClick = () => {
    if (!updateFile) {
      messageApi.warning("Please select a file");
      return;
    }

    setTwoFAAction("update");

    setLoading(true);
    axios
      .post("/email/otp", { email })
      .then(() => {
        messageApi.info("Verification code sent to your email");
        setIs2FAModalOpen(true);
        setIsOpen(false);
      })
      .catch(() => {
        messageApi.error("Failed to send OTP, try again");
      })
      .finally(() => setLoading(false));
  };

  // Handle 2FA verification result for delete
  const handle2FA = async (verified) => {
    if (!verified) {
      messageApi.warning("Verification failed, try again");
      return;
    }

    setIs2FAModalOpen(false);

    if (twoFAAction === "update") {
      await handleUpdate();
    }

    setTwoFAAction(null);
  };

  // Upload handlers (no 2FA here)
  const onFinish = (values) => {
    if (!values?.file?.fileList[0]?.originFileObj) {
      messageApi.open({
        type: "warning",
        content: "Please select file",
      });
      return;
    }
    setLoading(true);
    let data = new FormData();
    data.append("name", values.name);
    data.append("myfile", values.file.fileList[0].originFileObj);
    const environmentType = "/UploadBPGSFiles";
    axios
      .post(process.env.REACT_APP_API_URL + environmentType, data)
      .then(() => {
        messageApi.open({
          type: "success",
          content: "file uploaded successfully",
        });
        getData();
        setLoading(false);
        form.resetFields();
      })
      .catch((err) => {
        setLoading(false);
        console.log(err);
      });
  };

  const onFinishFailed = ({ errorFields }) => {};

  const columns = [
    {
      title: "No",
      dataIndex: "no",
      key: "no",
    },
    {
      title: "File",
      dataIndex: "file",
      key: "file",
    },
    {
      title: "Date & Time",
      dataIndex: "date_time",
      key: "date_time",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (data) => {
        return (
          <div style={{ cursor: "pointer" }}>
            <EditOutlined
              onClick={() => {
                setSelectedFile(data);
                setUpdateFile(null);
                setIsOpen(true);
              }}
              style={{
                marginRight: "10px",
                color: "#faad14",
                cursor: "pointer",
              }}
            />
            <DownloadOutlined
              onClick={() => downloadFile(data._id)}
              title="Download"
              style={{
                fontSize: "14px",
                color: "#167bff",
                marginRight: "10px",
              }}
            />
            <CopyOutlined
              onClick={() => copyDownloadFileLink(data._id)}
              title="Copy Link"
              style={{
                fontSize: "14px",
                color: "green",
                marginRight: "10px",
              }}
            />
            <Popconfirm
              title="Are you sure?"
              onConfirm={() => handleDeleteClick(data)}
              okText="Yes"
              cancelText="No"
            >
              <DeleteOutlined style={{ fontSize: "14px", color: "red" }} />
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {/* 2FA modal ONLY for delete */}
      <TwoFactAuth
        isModalOpen={is2FAModalOpen}
        setIsModalOpen={setIs2FAModalOpen}
        handle2FA={handle2FA}
      />
      <Modal
        title="Replace File"
        size="small"
        style={{ width: 300 }}
        open={isOpen}
        onOk={() => handleUpdateClick()}
        onCancel={() => {
          setIsOpen(false);
          setSelectedFile(null);
          setUpdateFile(null);
        }}
      >
        <Upload
          maxCount={1}
          fileList={updateFile ? [updateFile] : []}
          accept={ext ? `.${ext}` : null}
          beforeUpload={(file) => {
            setUpdateFile(file);
            return false;
          }}
        >
          <Button>Select Replacement File</Button>
        </Upload>
      </Modal>

      <Spin spinning={loading}>
        {contextHolder}
        <div style={{ marginBottom: "2rem" }}>
          <h3>Upload Files to Production</h3>
        </div>
        <Form
          form={form}
          name="basic"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          labelCol={{
            span: 2,
          }}
          wrapperCol={{
            span: 16,
          }}
        >
          <Form.Item
            label="File Name"
            name="name"
            rules={[
              {
                required: true,
                message: "Please input your File Name!",
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="File"
            name="file"
            rules={[{ required: true, message: "Please upload a file!" }]}
          >
            <Upload
              beforeUpload={() => false} // prevent auto upload, handle manually
              listType="picture-card"
              maxCount={1}
            >
              <div>
                <PlusOutlined />
                <div
                  style={{
                    marginTop: 8,
                  }}
                >
                  Select File
                </div>
              </div>
            </Upload>
          </Form.Item>

          <Form.Item
            labelCol={{
              span: 2,
            }}
            wrapperCol={{
              span: 16,
            }}
          >
            <Button type="primary" htmlType="submit">
              Upload File
            </Button>
          </Form.Item>
        </Form>
        <div style={{ marginTop: "25px" }}>
          <Table columns={columns} dataSource={ApiData} rowKey="id" />
        </div>
      </Spin>
    </div>
  );
};

export default UploadDataBPGS;
